/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, NgZone} from '@angular/core';
import * as _ from 'lodash';
import template from './team-edit-page.component.html';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {AdminResourceEditPageBase} from '../../common/controller/admin-resource-edit-page-base';
import {ADN_TARGETING_TYPES} from '../../targeting/targeting-constants';
import {TargetingEditorModalService} from '../../targeting/targeting-editor-modal.service';
import {AngularModalService} from '../../common/modal/angular-modal.service';
import {getAvailableTeamTypes, initFeesForParam, preTeamSaveFix} from './team-commons';
import {TeamObjectMembershipModalComponent} from './team-object-membership-modal.component';
import {TeamOwnershipModalComponent} from './team-ownership-modal.component';
import {FolderResource} from '../../../components/api/resources/resource/folder.resource';
import {SiteResource} from '../../../components/api/resources/resource/site.resource';
import {TeamGroupResource} from '../../../components/api/resources/resource/team-group.resource';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';

import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';

@Component({
  standalone: false,
  selector: 'adn-team-edit-page',
  template
})
export class TeamEditPageComponent extends AdminResourceEditPageBase {
  @Input() readOnly = false;

  applications: string[] = [];
  politicalApp = false;
  canSeeTechFees = false;
  canSeeTeamGroups = false;
  canSeeSiteLinks = false;
  showLabels = false;
  teamTypes: string[] = [];
  networkCurrencies: string[] = [];
  targetTypes = _.filter(ADN_TARGETING_TYPES, (tt: any) => tt.apiType !== 'THIRD_PARTY_AUDIENCE');
  allTeamGroups: any[] | null = null;
  sites: any[] | null = null;
  matchedSites: any[] = [];
  unownedSites: any[] = [];
  ownerSites: any[] = [];
  folders: any[] | null = null;
  matchedFolders: any[] = [];
  siteGroups: any[] | null = null;
  ownerSiteGroups: any[] = [];
  targetingResult: any = {data: false};

  private originalTeamGroupId: string | undefined;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(SiteResource) private readonly Site: any,
    @Inject(FolderResource) private readonly Folder: any,
    @Inject(TeamGroupResource) private readonly TeamGroup: any,
    private readonly angularModalService: AngularModalService,
    private readonly targetingEditorModalService: TargetingEditorModalService,
    private readonly cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.applications = _.filter(this.LocalUserPermissions.getApps(), (a: string) => a !== 'SELF_SERVICE');
    this.politicalApp = !!this.LocalNetworkProfile.get('politicalApp');
    if (this.politicalApp && this.applications.indexOf('POLITICAL') < 0) {
      this.applications.push('POLITICAL');
    }
    this.canSeeTechFees = this.LocalUserPermissions.hasNetwork('MANAGE_SYSTEM');
    this.canSeeTeamGroups = this.LocalUserPermissions.hasNetwork('MANAGE_SYSTEM')
      && LocalNetworkInfo.obtain().getInfo('teamGroupsCount') > 0;
    this.canSeeSiteLinks = this.LocalUserPermissions.hasNetwork('MANAGE_SYSTEM')
      || this.LocalUserPermissions.hasNetwork('MANAGE_PUBLISHING')
      || this.LocalUserPermissions.hasAny('AX_PUBLISHER_TEAM_ADMIN')
      || this.LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN');
    this.showLabels = this.LocalNetworkProfile.isPureMarketplacePlatform();
    this.teamTypes = getAvailableTeamTypes(this.LocalNetworkProfile);
    const defaultCurrency = this.LocalNetworkProfile.get('defaultCurrency');
    this.networkCurrencies = _.orderBy(this.LocalNetworkProfile.get('availableCurrencies'), (curr: string) => {
      return curr === defaultCurrency ? 0 : 1;
    });
  }

  get showTypeField(): boolean {
    return this.applications.indexOf('ADVERTISING') > -1 || this.politicalApp;
  }

  countryChangeHook = (): void => {
    this.markExternalFormDirty();
  };

  protected afterInitEditingModel(): void {
    this.editingModel.sites = _.map(this.editingModel.sites || [], 'id');
    this.editingModel.folders = _.map(this.editingModel.folders || [], 'id');
    this.editingModel.defaultTargeting = this.editingModel.defaultTargeting || {};
    this.afterSaveAfterInit();
    if (!this.isNew) {
      this.siteRefresh();
      this.folderRefresh();
    }
  }

  protected afterInitAfterSave(saveObj?: any): void {
    if (saveObj) {
      // Replace fee objects from the API response. applySave uses _.merge, which
      // merges multiCurrencyMonetaryAmounts by index and can duplicate currencies
      // when the saved list is a subset or differently ordered.
      if (saveObj.techFeeCpm !== undefined) {
        this.editingModel.techFeeCpm = _.cloneDeep(saveObj.techFeeCpm);
      }
      if (saveObj.creativeTagTechFeeCpm !== undefined) {
        this.editingModel.creativeTagTechFeeCpm = _.cloneDeep(saveObj.creativeTagTechFeeCpm);
      }
      this.afterSaveAfterInit();
      this.teamGroupQuery();
      return;
    }
    this.afterSaveAfterInit();
  }

  protected onBeforeSubmit(): void {
    const currentTeamGroupId = _.get(this.editingModel, ['teamGroup', 'id']);
    if (this.canSeeTeamGroups && (currentTeamGroupId || this.originalTeamGroupId) && (currentTeamGroupId !== this.originalTeamGroupId)) {
      this.TeamGroup.updateTeamToTeamGroup(currentTeamGroupId || this.originalTeamGroupId, this.editingModel.id, !currentTeamGroupId);
    }
    if (this.isNew) {
      LocalNetworkInfo.obtain().increment('teamsCount');
    }
  }

  protected syncEditingModelToApiModel(): void {
    // preTeamSaveFix converts ui* fee structures into API fields (targetingTypeTechFees,
    // layoutFees, etc.) and may null empty techFeeCpm values. Run it against clones on
    // the editing model so converted maps sync to the API model and survive post-save
    // re-init, while the live form keeps its bound fee objects.
    const live = {
      techFeeCpm: this.editingModel.techFeeCpm,
      creativeTagTechFeeCpm: this.editingModel.creativeTagTechFeeCpm,
      supplementaryFees: this.editingModel.supplementaryFees,
      uiLayoutFees: this.editingModel.uiLayoutFees,
      uiTargetingTypeTechFees: this.editingModel.uiTargetingTypeTechFees
    };
    this.editingModel.techFeeCpm = _.cloneDeep(live.techFeeCpm);
    this.editingModel.creativeTagTechFeeCpm = _.cloneDeep(live.creativeTagTechFeeCpm);
    this.editingModel.supplementaryFees = _.cloneDeep(live.supplementaryFees);
    this.editingModel.uiLayoutFees = _.cloneDeep(live.uiLayoutFees);
    this.editingModel.uiTargetingTypeTechFees = _.cloneDeep(live.uiTargetingTypeTechFees);

    preTeamSaveFix({model: this.editingModel});
    super.syncEditingModelToApiModel();

    this.editingModel.techFeeCpm = live.techFeeCpm;
    this.editingModel.creativeTagTechFeeCpm = live.creativeTagTechFeeCpm;
    this.editingModel.supplementaryFees = live.supplementaryFees;
    this.editingModel.uiLayoutFees = live.uiLayoutFees;
    this.editingModel.uiTargetingTypeTechFees = live.uiTargetingTypeTechFees;
  }

  addSupplementaryFee(): void {
    this.markExternalFormDirty();
    this.editingModel.supplementaryFees = this.editingModel.supplementaryFees || [];
    const supFee = {
      type: 'PERCENTAGE',
      name: 'Fee',
      amount: null
    };
    initFeesForParam(supFee, this.networkCurrencies);
    this.editingModel.supplementaryFees.unshift(supFee);
  }

  deleteFee(index: number): void {
    this.markExternalFormDirty();
    _.pullAt(this.editingModel.supplementaryFees, index);
    if (this.editingModel.supplementaryFees.length === 0) {
      this.addSupplementaryFee();
    }
  }

  removeLayoutFee(index: number): void {
    _.pullAt(this.editingModel.uiLayoutFees, index);
    if (this.editingModel.uiLayoutFees.length === 0) {
      this.addLayoutFee();
    }
    this.markExternalFormDirty();
  }

  addLayoutFee(): void {
    this.editingModel.uiLayoutFees = this.editingModel.uiLayoutFees || [];
    const defaultFee = {fee: {type: 'MULTI_CURRENCY_MONETARY'}};
    initFeesForParam(defaultFee.fee, this.networkCurrencies);
    this.editingModel.uiLayoutFees.push(defaultFee);
    this.markExternalFormDirty();
  }

  removeTargetingFee(index: number): void {
    _.pullAt(this.editingModel.uiTargetingTypeTechFees, index);
    if (this.editingModel.uiTargetingTypeTechFees.length === 0) {
      this.addTargetingFee();
    }
    this.markExternalFormDirty();
  }

  addTargetingFee(): void {
    this.editingModel.uiTargetingTypeTechFees = this.editingModel.uiTargetingTypeTechFees || [];
    const defaultFee = {fee: {type: 'MULTI_CURRENCY_MONETARY'}};
    initFeesForParam(defaultFee.fee, this.networkCurrencies);
    this.editingModel.uiTargetingTypeTechFees.push(defaultFee);
    this.markExternalFormDirty();
  }

  editTarget(targeting: any): void {
    this.targetingResult = {data: false};
    this.targetingEditorModalService.open({
      lineItemType: 'NORMAL',
      modelLineItem: _.cloneDeep(this.editingModel.data || {}),
      pageType: 'marketplaceProduct',
      reachEnabled: undefined,
      mandatoryTargets: undefined,
      availableTargets: null,
      overridableTargeting: undefined,
      targetingMode: null,
      targeting: targeting,
      isLockedDown: false
    }).then((wc: any) => {
      if (!wc) {
        return;
      }
      _.assign(targeting, wc.wc);
      // New object reference so adn-target-summary ngOnChanges rebuilds the summary.
      this.editingModel.defaultTargeting = _.cloneDeep(targeting);
      this.model.defaultTargeting = _.cloneDeep(targeting);
      if (!this.isNew) {
        this.model.savePartial('defaultTargeting').then((respLineItem: any) => {
          this.ngZone.run(() => {
            this.targetingResult.data = true;
            _.set(this.editingModel, 'validationWarnings', respLineItem.validationWarnings);
          });
        });
      } else {
        this.markExternalFormDirty();
      }
    });
  }

  editSiteOwnership(removeMode = false): void {
    const modalRef = this.angularModalService.show(TeamOwnershipModalComponent, {
      class: 'modal-lg',
      initialState: {
        obj: this.editingModel,
        removeMode,
        ownerSites: this.ownerSites,
        unownedSites: this.unownedSites
      }
    });

    (modalRef.content as TeamOwnershipModalComponent).onSaved.subscribe(() => {
      this.sites = null;
      this.siteRefresh();
      setTimeout(() => {
        this.editingModel.updateValidationWarnings?.();
      }, 500);
    });
  }

  editSiteMembership(): void {
    const modalRef = this.angularModalService.show(TeamObjectMembershipModalComponent, {
      class: 'modal-lg',
      initialState: {
        theResource: this.Site,
        translation: 'Site',
        obj: this.editingModel,
        objects: this.sites,
        matchedObjects: this.matchedSites
      }
    });

    (modalRef.content as TeamObjectMembershipModalComponent).onSaved.subscribe(() => {
      this.sites = null;
      this.siteRefresh();
      setTimeout(() => {
        this.editingModel.updateValidationWarnings?.();
      }, 500);
    });
  }

  editFolderMembership(): void {
    const modalRef = this.angularModalService.show(TeamObjectMembershipModalComponent, {
      class: 'modal-lg',
      initialState: {
        theResource: this.Folder,
        translation: 'Folder',
        obj: this.editingModel,
        objects: this.folders,
        matchedObjects: this.matchedFolders
      }
    });

    (modalRef.content as TeamObjectMembershipModalComponent).onSaved.subscribe(() => {
      this.folders = null;
      this.folderRefresh();
      setTimeout(() => {
        this.editingModel.updateValidationWarnings?.();
      }, 500);
    });
  }

  private afterSaveAfterInit(): void {
    if (!this.editingModel.type) {
      if (this.LocalUserPermissions.hasNetworkPermission('AX_ADVERTISER_ADMIN')) {
        this.editingModel.type = 'AX_ADVERTISER';
      } else if (this.teamTypes && this.teamTypes.length) {
        this.editingModel.type = this.teamTypes.indexOf('STANDARD') > -1 ? 'STANDARD' : this.teamTypes[0];
      } else {
        this.editingModel.type = 'STANDARD';
      }
    }
    this.editingModel.techFeeCpm = this.editingModel.techFeeCpm || {type: 'PERCENTAGE'};
    this.editingModel.creativeTagTechFeeCpm = this.editingModel.creativeTagTechFeeCpm || {type: 'MULTI_CURRENCY_MONETARY'};
    initFeesForParam(this.editingModel.techFeeCpm, this.networkCurrencies);
    initFeesForParam(this.editingModel.creativeTagTechFeeCpm, this.networkCurrencies);
    this.teamGroupQuery();
    this.initialiseLayoutFees();
    this.initTargetTypeFees();
    this.editingModel.supplementaryFees = _.get(this.editingModel, 'supplementaryFees', []);
    _.forEach(this.editingModel.supplementaryFees, (fee: any) => {
      initFeesForParam(fee, this.networkCurrencies);
    });
  }

  private teamGroupQuery(): void {
    const teamGroupsCount = LocalNetworkInfo.obtain().getInfo('teamGroupsCount');
    if (teamGroupsCount > 0 && this.canSeeTeamGroups && !this.allTeamGroups && !this.isNew) {
      this.TeamGroup.query({filterBy: 'type', filterByValue: this.editingModel.type}).$promise.then((page: any) => {
        // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
        deferMarkForCheck(this.cdr, () => {
          this.allTeamGroups = page.results;
          this.editingModel.teamGroup = _.find(this.allTeamGroups, (tg: any) => {
            return _.find(tg.teams, (t: any) => this.editingModel.id === t.id);
          }) || null;
          this.originalTeamGroupId = _.get(this.editingModel, ['teamGroup', 'id']);
        });
      });
    }
  }

  private initialiseLayoutFees(): void {
    const layoutFees = _.get(this.editingModel, 'layoutFees');
    this.editingModel.uiLayoutFees = _.isArray(layoutFees) ? layoutFees : [];
    this.editingModel.uiLayoutFees[0] = this.editingModel.uiLayoutFees[0] ? this.editingModel.uiLayoutFees[0] : {};
    this.editingModel.uiLayoutFees = _.map(this.editingModel.uiLayoutFees, (layoutFee: any) => {
      const adjFee = _.cloneDeep(layoutFee) || {};
      adjFee.fee = adjFee.fee || {type: 'MULTI_CURRENCY_MONETARY'};
      initFeesForParam(adjFee.fee, this.networkCurrencies);
      return adjFee;
    });
  }

  private initTargetTypeFees(): void {
    this.editingModel.uiTargetingTypeTechFees = _.get(this.editingModel, 'targetingTypeTechFees', []);
    this.editingModel.uiTargetingTypeTechFees = _.map(this.editingModel.uiTargetingTypeTechFees, (valFee: any, key: string) => {
      return {apiType: key, fee: valFee};
    });
    if (_.isEmpty(this.editingModel.uiTargetingTypeTechFees)) {
      this.editingModel.uiTargetingTypeTechFees = [];
    }
    if (!this.editingModel.uiTargetingTypeTechFees.length) {
      this.editingModel.uiTargetingTypeTechFees.push({fee: {type: 'MULTI_CURRENCY_MONETARY'}});
    }
    _.forEach(this.editingModel.uiTargetingTypeTechFees, (fee: any) => {
      initFeesForParam(fee.fee, this.networkCurrencies);
    });
  }

  private siteRefresh(): void {
    if (this.applications.indexOf('ADVERTISING') < 0 || !this.editingModel?.id) {
      return;
    }
    this.Site.query({pageSize: 1000, team: this.editingModel.id}).$promise.then((page: any) => {
      this.ngZone.run(() => {
        this.matchedSites = _.map(page.results, (sg: any) => {
          const indexedTeams = _.keyBy(sg.teams, 'id');
          return _.assign({}, _.pick(sg, ['name', 'description', 'id', 'teams', 'ownerTeam']), {
            indexedTeams: indexedTeams,
            isOwned: !!_.get(sg.ownerTeam, ['id']),
            isOwner: _.get(sg.ownerTeam, ['id']) === this.editingModel.id
          });
        });
        this.unownedSites = _.filter(this.matchedSites, {isOwned: false});
        this.ownerSites = _.filter(this.matchedSites, {isOwner: true});
      });
    });
    this.Site.query({pageSize: 1000}).$promise.then((page: any) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.sites = page.results;
      });
    });
  }

  private folderRefresh(): void {
    if (this.applications.indexOf('DATA') < 0 || !this.editingModel?.id) {
      return;
    }
    this.Folder.query({pageSize: 1000}).$promise.then((page: any) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.folders = _.map(page.results, (sg: any) => {
          const indexedTeams = _.keyBy(sg.teams, 'id');
          return _.assign({}, _.pick(sg, ['name', 'description', 'id', 'teams']), {
            indexedTeams: indexedTeams,
            isMatched: !!indexedTeams[this.editingModel.id]
          });
        });
        this.matchedFolders = _.filter(this.folders, {isMatched: true});
      });
    });
  }
}
